import { DialogComponent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { preloadImage, isMobileBreakpoint } from '@theme/utilities';
import '@theme/fly-to-cart';

/**
 * A custom element that manages a wishlist drawer with fly-to-cart animation
 * and quick view / add-to-cart support via the theme's existing quick-add-dialog.
 *
 * @extends {DialogComponent}
 */
class WishlistDrawerComponent extends DialogComponent {
  constructor() {
    super();
    this.localStorageKey = 'sr_wishlist_horizon_v1';
    this.headerObserver = null;
    this.cardObserver = null;
    this.currencyCode = '';
    this.showBtnText = false;
    this.showDividers = false;
    this.removeBtnText = '';
    this.addToWishlist = '';
    this.inWishlist = '';
    this.buttonStyle = '';
    this.headerStyle = '';
    this.headerText = '';
    this.buttonColorScheme = '';
    this.shouldOpen = false;
    this.showFlyAnimation = true;
    /** @type {Map<string, Element>} */
    this.#quickViewCache = new Map();
    /** @type {AbortController | null} */
    this.#quickViewAbortController = null;
  }

  /** @type {Map<string, Element>} */
  #quickViewCache;

  /** @type {AbortController | null} */
  #quickViewAbortController;

  connectedCallback() {
    super.connectedCallback();
    this.initializeSettings();
    this.setupWishlistSystem();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  initializeSettings() {
    this.currencyCode = this.dataset.currencyCode || Shopify?.currency?.active || '';
    this.showBtnText = this.dataset.displayButtonText === 'true';
    this.showDividers = this.dataset.displayDividers === 'true';
    this.removeBtnText = this.dataset.textButtonRemove || 'Remove';
    this.addToWishlist = this.dataset.textAddToWishlist || 'Add to Wishlist';
    this.inWishlist = this.dataset.textInWishlist || 'In Wishlist';
    this.buttonStyle = this.dataset.buttonStyle || 'button-secondary';
    this.headerStyle = this.dataset.headerStyle || 'icon';
    this.headerText = this.dataset.textWishlist || 'Wishlist';
    this.buttonColorScheme = this.dataset.buttonColorScheme || 'color-scheme-1';
    this.shouldOpen = this.dataset.openOnAdd === 'true';
    this.showFlyAnimation = this.dataset.showFlyAnimation !== 'false';
    this.addToCartText = this.dataset.textAddToCart || 'Add to cart';
    this.addingText = this.dataset.textAdding || 'Adding...';
    this.addedText = this.dataset.textAdded || 'Added to cart';
    this.chooseText = this.dataset.textChoose || 'Choose options';
  }

  setupWishlistSystem() {
    this.injectWishlistHeader();
    this.insertProductWishlistBtn();
    this.insertCardWishlistBtn();
    this.initWishlist();
    this.buildWishlistDrawer();
    this.enableWishlistBackdropClose();
    this.watchForHeaderChanges();

    // Make the click handlers globally available
    window.onClickSrWishlistButton = this.onClickSrWishlistButton.bind(this);
    window.onClickSrWishlistQuickView = this.onClickSrWishlistQuickView.bind(this);
    window.onClickSrWishlistAddToCart = this.onClickSrWishlistAddToCart.bind(this);

    // Migrate any stored items that are missing variant data
    this.#migrateWishlistItems();
  }

  cleanup() {
    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }
    if (this.cardObserver) {
      this.cardObserver.disconnect();
      this.cardObserver = null;
    }
    this.#quickViewAbortController?.abort();
    if (window.onClickSrWishlistButton) delete window.onClickSrWishlistButton;
    if (window.onClickSrWishlistQuickView) delete window.onClickSrWishlistQuickView;
    if (window.onClickSrWishlistAddToCart) delete window.onClickSrWishlistAddToCart;
  }

  /**
   * Migrates stored wishlist items that are missing variants_count/variant_id
   * by re-fetching their product data in the background, then rebuilding the drawer.
   */
  async #migrateWishlistItems() {
    const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
    const needsMigration = wishlist.filter((item) => item.variants_count == null);

    if (!needsMigration.length) return;

    const updated = await Promise.all(
      wishlist.map(async (item) => {
        if (item.variants_count != null) return item;

        try {
          const res = await fetch(`/products/${item.handle}.js`);
          if (!res.ok) return item;
          const product = await res.json();

          return {
            ...item,
            variants_count: product.variants.length,
            variant_id: product.variants[0]?.id
          };
        } catch {
          return item;
        }
      })
    );

    localStorage.setItem(this.localStorageKey, JSON.stringify(updated));
    this.buildWishlistDrawer();
  }

  /**
   * Formats a price in cents using the customer's local currency via Intl.NumberFormat.
   * With Shopify Markets, prices from the API are already in the customer's currency,
   * so we just need to format them correctly.
   * @param {number} cents - Price in cents
   * @returns {string}
   */
  formatMoney(cents) {
    if (!cents) return '';
    return Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: this.currencyCode,
      currencyDisplay: 'narrowSymbol'
    }).format(cents / 100);
  }

  buildWishlistButtonHTML(className = '', showText = false) {
    const addClass = showText ? 'add-to-wishlist-text' : 'visually-hidden';
    const removeClass = showText ? 'add-to-wishlist-text--added' : 'visually-hidden';

    return `
    <button 
      class="button-wishlist ${className.trim()}" 
      type="button"
      onclick="onClickSrWishlistButton(this, event)"
      aria-controls="sr-wishlist-drawer"
      style="display: none;">
      <span data-add>
        <span class="svg-wrapper wishlist-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M13.85,3c-2.58,0-3.85,2.55-3.85,2.55,0,0-1.27-2.55-3.85-2.55-2.1,0-3.76,1.76-3.78,3.85-.04,4.35,3.45,7.44,7.28,10.04.22.15.5.15.72,0,3.83-2.6,7.32-5.69,7.28-10.04-.02-2.09-1.68-3.85-3.78-3.85h0Z"/>
          </svg>
        </span>
        <span class="${addClass}">${this.addToWishlist}</span>
      </span>
      <span data-remove>
        <span class="svg-wrapper wishlist-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M13.85,3c-2.58,0-3.85,2.55-3.85,2.55,0,0-1.27-2.55-3.85-2.55-2.1,0-3.76,1.76-3.78,3.85-.04,4.35,3.45,7.44,7.28,10.04.22.15.5.15.72,0,3.83-2.6,7.32-5.69,7.28-10.04-.02-2.09-1.68-3.85-3.78-3.85h0Z"/>
          </svg>
        </span>
        <span class="${removeClass}">${this.inWishlist}</span>
      </span>
    </button>`;
  }

  /**
   * Builds the action button HTML for a wishlist item.
   * Single-variant products get an "Add to cart" button; multi-variant get "Quick view".
   * Falls back gracefully if quick-add-dialog is unavailable or migration is still pending.
   * @param {object} product - The stored wishlist product object
   * @param {boolean} hasQuickAdd - Whether the theme's quick-add-dialog is present
   * @returns {string}
   */
  buildItemActionButtonHTML(product, hasQuickAdd) {
    // variants_count may be null if migration is still in progress — render nothing for now,
    // the drawer will rebuild once migration completes.
    if (product.variants_count == null) return '';

    if (product.variants_count === 1) {
      return `
        <button
          class="wishlist-item__action-btn wishlist-item__add-to-cart button-unstyled"
          type="button"
          onclick="onClickSrWishlistAddToCart(this, event)"
          data-variant-id="${product.variant_id}"
          data-product-title="${product.title}"
          aria-label="${this.addToCartText} ${product.title}"
        >
          ${this.addToCartText}
        </button>`;
    }

    if (hasQuickAdd) {
      return `
        <button
          class="wishlist-item__action-btn wishlist-item__quick-view button-unstyled"
          type="button"
          onclick="onClickSrWishlistQuickView(this, event)"
          data-product-url="${product.url}"
          data-product-title="${product.title}"
          aria-label="${this.chooseText} ${product.title}"
        >
          ${this.chooseText}
        </button>`;
    }

    return '';
  }

  injectWishlistHeader() {
    if (document.querySelector('#wishlist-header')) return;

    const headerActions = document.querySelector('.header header-actions .cart-drawer');
    if (!headerActions) return;

    const link = document.createElement('a');
    link.id = 'wishlist-header';
    link.className = `wishlist-button header-actions__action header-actions__wishlist-icon--${this.headerStyle} header-actions__text-style`;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-controls', 'sr-wishlist-drawer');

    link.innerHTML = `
      <span class="wishlist-button-text mobile:hidden${this.headerStyle === 'icon' ? ' desktop:hidden' : ''}">${this.headerText}</span>
      <span class="svg-wrapper${this.headerStyle === 'text' ? ' desktop:hidden' : ''}">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M13.85,3c-2.58,0-3.85,2.55-3.85,2.55,0,0-1.27-2.55-3.85-2.55-2.1,0-3.76,1.76-3.78,3.85-.04,4.35,3.45,7.44,7.28,10.04.22.15.5.15.72,0,3.83-2.6,7.32-5.69,7.28-10.04-.02-2.09-1.68-3.85-3.78-3.85h0Z"/>
        </svg>
      </span>
      <div class="wishlist-bubble" data-maintain-ratio>
        <span class="wishlist-bubble__background">
          <span class="visually-hidden">Total items in wishlist: <span class="wishlist-count">0</span></span>
          <span class="wishlist-bubble__text-count" aria-hidden="true"><span class="wishlist-count">0</span></span>
        </span>
      </div>
    `;

    link.addEventListener('click', () => {
      this.open();
    });

    headerActions.before(link);
  }

  watchForHeaderChanges() {
    const headerActions = document.querySelector('.header header-actions');
    if (!headerActions) return;

    this.headerObserver = new MutationObserver(() => {
      if (!document.querySelector('#wishlist-header')) {
        this.injectWishlistHeader();
        this.initWishlist();
      }
    });

    this.headerObserver.observe(headerActions, {
      childList: true,
      subtree: true
    });
  }

  insertProductWishlistBtn() {
    if (this.dataset.displayProductButton === 'false') return;

    // Assign data-product-handle from current page URL
    const productTemplate = document.querySelector('[data-template="product"] .product-information');
    if (productTemplate) {
      const currentUrl = window.location.pathname;
      const match = currentUrl.match(/\/products\/([^\/?#]+)/);
      const productHandle = match?.[1];

      if (productHandle && !productTemplate.dataset.productHandle) {
        productTemplate.setAttribute('data-product-handle', productHandle);
      }
    }

    const productForm = document.querySelector('.product-information add-to-cart-component');
    if (!productForm) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'button-wishlist-wrapper';

    const wishlistButton = document.createElement('div');
    wishlistButton.innerHTML = this.buildWishlistButtonHTML(`button wishlist-button--custom ${this.buttonStyle}`, this.showBtnText);

    const parent = productForm.parentElement;
    parent.insertBefore(wrapper, productForm.nextSibling);
    wrapper.appendChild(productForm);
    wrapper.appendChild(wishlistButton.firstElementChild);
  }

  insertCardWishlistBtn() {
    if (this.dataset.displayCardButton === 'false') return;

    const setProductHandle = (card) => {
      const productUrl = card.querySelector('a')?.getAttribute('href');
      const match = productUrl?.match(/\/products\/([^\/?#]+)/);
      const handle = match?.[1];

      if (handle && !card.dataset.productHandle) {
        card.dataset.productHandle = handle;
      }
    };

    const injectButtons = (container = document) => {
      container.querySelectorAll('.product-card').forEach((card) => {
        setProductHandle(card);

        const cardInner = card.querySelector('.card-gallery');
        if (!cardInner) return;

        if (!cardInner.querySelector('.button-wishlist')) {
          cardInner.insertAdjacentHTML(
            'beforeend',
            this.buildWishlistButtonHTML(`product-card__button-wishlist ${this.buttonColorScheme}`, false)
          );
        }
      });
    };

    injectButtons();
    this.initWishlist();

    this.cardObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          if (node.matches?.('.product-card')) {
            injectButtons(node.parentElement || node);
            this.initWishlist();
          }

          const nested = node.querySelectorAll?.('.product-card');
          if (nested?.length) {
            injectButtons(node);
            this.initWishlist();
          }
        });
      });
    });

    this.cardObserver.observe(document.body, { childList: true, subtree: true });
  }

  initWishlist() {
    const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];

    document.querySelectorAll('.button-wishlist').forEach((btn) => {
      const productHandle = btn.closest('[data-product-handle]')?.dataset.productHandle;
      const isWishlisted = wishlist.some((item) => item.handle === productHandle);

      btn.querySelector('[data-add]').style.display = isWishlisted ? 'none' : 'flex';
      btn.querySelector('[data-remove]').style.display = isWishlisted ? 'flex' : 'none';
      btn.classList.toggle('in-wishlist', isWishlisted);
      btn.style.display = 'flex';
    });

    document.querySelectorAll('.wishlist-count').forEach((el) => el.textContent = `${wishlist.length}`);
    document.querySelectorAll('.wishlist-bubble').forEach((bubble) => {
      bubble.classList.toggle('is-visible', wishlist.length > 0);
    });
  }

  buildWishlistDrawer() {
    const drawer = this;
    const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
    const drawerEmpty = drawer.querySelector('.wishlist-drawer__empty');
    const drawerContent = drawer.querySelector('.wishlist-drawer__content');
    const drawerProductList = drawer.querySelector('.wishlist-drawer__items');

    drawerProductList.innerHTML = '';

    if (!wishlist.length) {
      drawer.classList.add('wishlist-empty');
      drawerEmpty.style.display = 'flex';
      drawerContent.style.display = 'none';
      return;
    }

    drawer.classList.remove('wishlist-empty');
    drawerEmpty.style.display = 'none';
    drawerContent.style.display = 'flex';

    const hasQuickAdd = !!document.getElementById('quick-add-dialog');

    drawerProductList.innerHTML = wishlist.map((product) => `
      <li class="wishlist-item ${this.showDividers ? 'wishlist-item--dividers' : ''}">
        <div class="wishlist-item__row">
          <a href="${product.url}" class="wishlist-item__media-link">
            <img
              class="wishlist-item__media"
              src="${product.featured_image}&width=250"
              srcset="${product.featured_image}&width=250 250w"
              sizes="250px"
              width="250"
              height="auto"
              alt="${product.title}"
              loading="lazy"
            />
          </a>
          <div class="wishlist-item__details">
            <div>
              <a href="${product.url}" class="wishlist-item__product-title">${product.title}</a>
              <div class="wishlist-item__price">
                <span class="visually-hidden">Sale price</span>
                <span>${this.formatMoney(product.price)}</span>
                <span class="compare-at-price" style="${product.compare_at_price > product.price ? '' : 'display: none;'}">
                  <span class="visually-hidden">Regular price</span>
                  ${this.formatMoney(product.compare_at_price)}
                </span>
              </div>
            </div>
            ${this.buildItemActionButtonHTML(product, hasQuickAdd)}
          </div>
          <button class="wishlist-item__remove button-unstyled" type="button" aria-label="${this.removeBtnText}" data-product-handle="${product.handle}">
            <span class="visually-hidden">${this.removeBtnText}</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
        </div>
      </li>`).join('');

    drawerProductList.querySelectorAll('.wishlist-item__remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
        const updated = wishlist.filter((item) => item.handle !== btn.dataset.productHandle);
        localStorage.setItem(this.localStorageKey, JSON.stringify(updated));
        this.initWishlist();
        this.buildWishlistDrawer();
      });
    });
  }

  enableWishlistBackdropClose() {
    const wishlistDialog = this.querySelector('dialog');
    if (!wishlistDialog) return;

    wishlistDialog.addEventListener('click', (e) => {
      const rect = wishlistDialog.getBoundingClientRect();
      const clickedOutside =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;

      if (clickedOutside) {
        this.close();
      }
    });
  }

  /**
   * Animates a product image flying to the wishlist icon using the theme's fly-to-cart element.
   * @param {HTMLElement} sourceButton - The button that was clicked
   * @param {string} productImage - The product image URL
   */
  #animateFlyToWishlist(sourceButton, productImage) {
    if (!this.showFlyAnimation) return;

    const wishlistIcon = document.querySelector('#wishlist-header');
    if (!wishlistIcon || !sourceButton || !productImage) return;

    const flyEl = document.createElement('fly-to-cart');
    flyEl.style.setProperty('background-image', `url(${productImage})`);
    flyEl.source = sourceButton;
    flyEl.destination = wishlistIcon;

    document.body.appendChild(flyEl);
  }

  /**
   * Fetches a product page and returns the parsed HTML document.
   * @param {string} url - The product URL to fetch
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(url) {
    this.#quickViewAbortController?.abort();
    this.#quickViewAbortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#quickViewAbortController.signal });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Quick view fetch failed:', err);
      return null;
    } finally {
      this.#quickViewAbortController = null;
    }
  }

  /**
   * Directly adds a single-variant product to the cart via the Cart API,
   * then dispatches a cart:update event so the theme's cart drawer and bubble refresh.
   * @param {HTMLButtonElement} btn - The add to cart button
   * @param {Event} event
   */
  async onClickSrWishlistAddToCart(btn, event) {
    event.preventDefault();
    event.stopPropagation();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const originalText = btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    btn.textContent = this.addingText;

    try {
      // Include the cart drawer section ID so /cart/add.js returns updated section HTML
      const cartItemsComponent = document.querySelector('cart-items-component[data-drawer]');
      const sectionId = cartItemsComponent?.dataset.sectionId;

      const body = { id: variantId, quantity: 1 };
      if (sectionId) {
        body.sections = sectionId;
        body.sections_url = window.location.pathname;
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!addResponse.ok) {
        const data = await addResponse.json();
        throw new Error(data.description || data.message || addResponse.statusText);
      }

      const addData = await addResponse.json();
      const cart = await fetch('/cart.js').then((r) => r.json());

      // Dispatch cart:update with the same shape the theme expects
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'sr-wishlist-add-to-cart',
            data: {
              source: 'wishlist',
              variantId,
              itemCount: cart.item_count,
              sections: addData.sections ?? {}
            }
          }
        })
      );

      btn.textContent = this.addedText;
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }, 2000);

    } catch (err) {
      console.error('Failed to add to cart:', err);
      btn.textContent = originalText;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  }

  /**
   * Opens the theme's quick-add-dialog with the wishlist item's product page content.
   * Mirrors the logic of QuickAddComponent.handleClick / updateQuickAddModal.
   * @param {HTMLButtonElement} btn - The quick view button
   * @param {Event} event
   */
  async onClickSrWishlistQuickView(btn, event) {
    event.preventDefault();
    event.stopPropagation();

    const productUrl = btn.dataset.productUrl;
    if (!productUrl) return;

    const quickAddDialog = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!quickAddDialog || !modalContent) return;

    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    try {
      let productGrid = this.#quickViewCache.get(productUrl);

      if (!productGrid) {
        const html = await this.#fetchProductPage(productUrl);
        if (!html) return;

        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) return;

        productGrid = gridElement.cloneNode(true);
        this.#quickViewCache.set(productUrl, productGrid);
      }

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));

      if (isMobileBreakpoint()) {
        const productDetails = freshContent.querySelector('.product-details');
        const productFormComponent = freshContent.querySelector('product-form-component');
        const variantPicker = freshContent.querySelector('variant-picker');
        const productPrice = freshContent.querySelector('product-price');

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');

        const productTitle = document.createElement('a');
        productTitle.textContent = btn.dataset.productTitle || '';
        productTitle.href = productUrl;
        productHeader.appendChild(productTitle);

        if (productPrice) productHeader.appendChild(productPrice);
        freshContent.appendChild(productHeader);
        if (variantPicker) freshContent.appendChild(variantPicker);
        if (productFormComponent) freshContent.appendChild(productFormComponent);
        productDetails?.remove();
      }

      morph(modalContent, freshContent);

      // Close the wishlist drawer before opening quick-add so they don't overlap
      this.close();
      quickAddDialog.showDialog();

    } finally {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }

  async onClickSrWishlistButton(btn, event) {
    event.preventDefault();
    event.stopPropagation();

    const productHandle = btn.closest('[data-product-handle]')?.dataset.productHandle;
    let wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
    const isWishlisted = wishlist.some((item) => item.handle === productHandle);

    if (isWishlisted) {
      wishlist = wishlist.filter((item) => item.handle !== productHandle);
    } else {
      try {
        const res = await fetch(`/products/${productHandle}.js`);
        const product = await res.json();

        if (product.featured_image) {
          preloadImage(product.featured_image);
        }

        wishlist.push({
          id: product.id,
          handle: productHandle,
          url: product.url,
          title: product.title,
          compare_at_price: product.compare_at_price,
          compare_at_price_varies: product.compare_at_price_varies,
          price: product.price,
          featured_image: product.featured_image,
          vendor: product.vendor,
          variants_count: product.variants.length,
          variant_id: product.variants[0]?.id,
          time: Date.now()
        });

        this.#animateFlyToWishlist(btn, product.featured_image + '&width=100');

        // Warm the quick view cache in the background for multi-variant products
        if (product.variants.length > 1 && document.getElementById('quick-add-dialog')) {
          this.#fetchProductPage(product.url).then((html) => {
            if (!html) return;
            const gridElement = html.querySelector('[data-product-grid-content]');
            if (gridElement) this.#quickViewCache.set(product.url, gridElement.cloneNode(true));
          });
        }

      } catch (err) {
        console.error('Failed to fetch product data:', err);
      }
    }

    localStorage.setItem(this.localStorageKey, JSON.stringify(wishlist));
    this.initWishlist();
    this.buildWishlistDrawer();

    if (!isWishlisted && this.shouldOpen && !this.querySelector('dialog')?.open) {
      const delay = this.showFlyAnimation ? 600 : 60;
      setTimeout(() => {
        requestAnimationFrame(() => {
          this.open();
        });
      }, delay);
    }
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}